// @flow

import React, {useMemo} from 'react';
import {Grid, Typography, makeStyles} from '@material-ui/core';

import {colors} from '../../../../../assets/styles/colors';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import MultiOptionPreview from './MultiOptionPreview/MultiOptionPreview';

import {DAYS} from '../../constants/allowed-inputs';
import type {Theme} from '@material-ui/core';
import {INPUT_TYPE, type CheckoutInput} from '../../../../../entities';
import {allowedDaysToName} from '../../utils';
type Props = {
  input: CheckoutInput,
};

const useStyles = makeStyles((theme: Theme) => ({
  preview: {
    pointerEvents: 'none',
    padding: theme.spacing(2),
    background: 'white',
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    // backgroundColor: 'white',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      maxWidth: '22rem',
    },
  },
  title: {
    color: colors.grey900,
    fontWeight: 600,
    fontSize: 16,
    maxWidth: '100%',
    wordBreak: 'break-word',
  },
  subtitle: {
    color: colors.grey900,
    fontWeight: 500,
    fontSize: 14,
  },
  optional: {
    color: colors.grey800,
    fontSize: 14,
    maxWidth: '100%',
    wordBreak: 'break-word',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
}));

export default function CheckoutInputPreview({input}: Props) {
  const {inputType, name, description, schema} = input;
  const classes = useStyles();

  const buildAllowedDays = useMemo(() => {
    if (schema?.allowed_weekdays?.length) {
      return allowedDaysToName(schema.allowed_weekdays)
        .sort((a, b) => {
          const idxA = DAYS.findIndex(el => el.name === a);
          const idxB = DAYS.findIndex(el => el.name === b);
          return idxA - idxB;
        })
        .join(', ');
    }
    return '--';
  }, [schema]);

  if ([INPUT_TYPE.TEXT, INPUT_TYPE.NUMBER].includes(inputType)) {
    return (
      <div className={classes.preview}>
        <div className={classes.content}>
          <Typography className={classes.title}>{name}</Typography>
          {description && (
            <Typography className={classes.optional}>{description}</Typography>
          )}
          <CustomTextField />
        </div>
      </div>
    );
  }

  if (INPUT_TYPE.CHECKLIST === inputType) {
    return (
      <div className={classes.preview}>
        <div className={classes.content}>
          <Typography className={classes.title}>{name}</Typography>
          {description ? (
            <Typography className={classes.optional}>{description}</Typography>
          ) : (
            <Typography className={classes.optional}>
              Selecciona un máximo de{' '}
              {schema ? schema.max_selected_options : '-'} opciones
            </Typography>
          )}
          <MultiOptionPreview options={schema ? schema.value : []} />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.preview}>
      <div className={classes.content}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography className={classes.subtitle}>
              Nombre del campo
            </Typography>
            <Typography className={classes.optional}>{name}</Typography>
          </Grid>
          {description && (
            <Grid item xs={12}>
              <Typography className={classes.subtitle}>
                Instrucciones/Texto Adicional
              </Typography>
              <Typography className={classes.optional}>
                {description}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography className={classes.subtitle}>
              Días habilitados{' '}
            </Typography>
            <Typography className={classes.optional}>
              {buildAllowedDays}
            </Typography>
          </Grid>
        </Grid>
      </div>
    </div>
  );
}
